import { Grid, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, Chip, Stack, SelectChangeEvent, Tabs, Tab } from '@mui/material';
import { Header } from 'components/Header/Header';
import { newServer } from '../../constants/constants';
import React, { useState, useEffect } from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const DirectorySettings: React.FC = () => {
  const [isRatingEnabled, setIsRatingEnabled] = useState<boolean>(false);
  const FONT_STYLE = { fontFamily: 'Poppins, sans-serif' };
  
  interface LawyerType {
    id: number | { id: number };
    title: string;
    description: string;
    handles: string;
    example: string;
    skills?: Skill[]; // Add skills array to lawyer type
    translations?: Translation[]; // Add translations array
  }
  
  interface Translation {
    id?: number;
    professional_type_id?: number;
    language_code: string;
    title: string;
    description: string;
    handles: string;
    example: string;
  }
  
  interface Skill {
    id: number;
    nombre: string;
    descriptions?: string;
    date_created?: string;
    submerchant_id?: number;
    translations?: SkillTranslation[];
  }
  
  interface SkillTranslation {
    id?: number;
    professional_skill_id?: number;
    language_code: string;
    nombre: string;
    descriptions?: string;
  }
  
  interface Language {
    id: number;
    nombre: string;
    codigo?: string;
    date_created?: string;
    submerchant_id?: number;
    translations?: LanguageTranslation[];
  }
  
  interface LanguageTranslation {
    id?: number;
    language_id?: number;
    language_code: string;
    nombre: string;
  }
  
  interface TypeSkillRelation {
    id_types: number;
    id_skills: number;
    type_title?: string;
    skill_name?: string;
  }
  
  interface FormDataType {
    title: string;
    description: string;
    handles: string;
    example: string;
    translations: Translation[];
  }
  
  const [lawyerTypes, setLawyerTypes] = useState<LawyerType[]>([]);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<number | ''>(''); // Cambiado de array a valor único
  const [typeSkillRelations, setTypeSkillRelations] = useState<TypeSkillRelation[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataType>({ 
    title: '', 
    description: '', 
    handles: '', 
    example: '',
    translations: [
      { language_code: 'en', title: '', description: '', handles: '', example: '' },
      { language_code: 'es', title: '', description: '', handles: '', example: '' }
    ]
  });
  const [editId, setEditId] = useState<number | null>(null);
  const [skillsOpen, setSkillsOpen] = useState<boolean>(false);
  const [currentTypeId, setCurrentTypeId] = useState<number | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string>('en');
  const [tabValue, setTabValue] = useState<number>(0);
  
  // Nueva funcionalidad para Skills y Languages
  const [mainTabValue, setMainTabValue] = useState<number>(0); // 0: Types, 1: Skills, 2: Languages
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [allLanguages, setAllLanguages] = useState<Language[]>([]);
  
  // Estados para Skills
  const [skillOpen, setSkillOpen] = useState<boolean>(false);
  const [skillFormData, setSkillFormData] = useState<{
    nombre: string;
    descriptions: string;
    translations: SkillTranslation[];
  }>({
    nombre: '',
    descriptions: '',
    translations: [
      { language_code: 'en', nombre: '', descriptions: '' },
      { language_code: 'es', nombre: '', descriptions: '' }
    ]
  });
  const [skillEditId, setSkillEditId] = useState<number | null>(null);
  const [skillTabValue, setSkillTabValue] = useState<number>(0);
  
  // Estados para Languages
  const [languageOpen, setLanguageOpen] = useState<boolean>(false);
  const [languageFormData, setLanguageFormData] = useState<{
    nombre: string;
    codigo: string;
    translations: LanguageTranslation[];
  }>({
    nombre: '',
    codigo: '',
    translations: [
      { language_code: 'en', nombre: '' },
      { language_code: 'es', nombre: '' }
    ]
  });
  const [languageEditId, setLanguageEditId] = useState<number | null>(null);
  const [languageTabValue, setLanguageTabValue] = useState<number>(0);

  // Fetch all data when component mounts
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = (): void => {
    // Fetch directory enabled setting
    axios.get(`${newServer}/directory/enabled`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.data;
      })
      .then((data) => setIsRatingEnabled((data as { isRatingEnabled: boolean }).isRatingEnabled))
      .catch((err) => console.error('Error fetching settings:', err));
    
    // Fetch lawyer types
    axios.get(`${newServer}/lawyer-types/get-all-lawyer-types`)
      .then((response) => {
        const types = response.data as LawyerType[];
        setLawyerTypes(types);
        
        // After fetching types, get all type-skill relations
        return axios.get(`${newServer}skill/type-skills`);
      })
      .then((response) => {
        const relations = response.data as TypeSkillRelation[];
        setTypeSkillRelations(relations);
        
        // Update lawyer types with their skills
        setLawyerTypes(prevTypes => {
          return prevTypes.map(type => {
            const typeSkills = relations
              .filter(rel => rel.id_types === type.id)
              .map(rel => ({
                id: rel.id_skills,
                nombre: rel.skill_name || ''
              }));
            
            return {
              ...type,
              skills: typeSkills
            };
          });
        });
      })
      .catch((err) => console.error('Error fetching data:', err));
    
    // Fetch all available skills
    axios.get(`${newServer}skill/skills`)
      .then((response) => setAvailableSkills(response.data as Skill[]))
      .catch((err) => console.error('Error fetching skills:', err));
    
    // Fetch all skills for management
    fetchAllSkills();
    
    // Fetch all languages for management
    fetchAllLanguages();
  };
  
  // Nueva función para obtener todos los skills
  const fetchAllSkills = (): void => {
    axios.get(`${newServer}directory/`)
      .then((response) => {
        const data = response.data as { skills: Skill[] };
        setAllSkills(data.skills);
      })
      .catch((err) => console.error('Error fetching all skills:', err));
  };
  
  // Nueva función para obtener todos los languages
  const fetchAllLanguages = (): void => {
    axios.get(`${newServer}directory/`)
      .then((response) => {
        const data = response.data as { lenguages: Language[] };
        setAllLanguages(data.lenguages);
      })
      .catch((err) => console.error('Error fetching all languages:', err));
  };

  const handleToggle = (): void => {
    const newState = !isRatingEnabled;
    setIsRatingEnabled(newState);

    axios.put(`${newServer}/directory`, { setting_enable_rating: newState })
      .then(() => toast.success('Rating setting updated successfully'))
      .catch((error) => {
        console.error('Error updating setting:', error);
        toast.error('Failed to update rating setting');
      });
  };

  const handleOpen = (lawyer: LawyerType | null = null, id: number | null = null): void => {
    setEditId(id);
    setSelectedSkill(''); // Resetear a valor vacío
    setTabValue(0); // Reset to first tab (English)
    
    if (lawyer && id) {
      // Editing mode - fetch full data with translations
      axios.get(`${newServer}/lawyer-types/get-lawyer-type/${id}?include_translations=true`)
        .then((response) => {
          const typeData = response.data as LawyerType & { translations?: Translation[] };
          const translations = typeData.translations || [];
          
          // Ensure we have both languages
          const enTranslation: Translation = translations.find((t: Translation) => t.language_code === 'en') || 
            { language_code: 'en', title: typeData.title, description: typeData.description, handles: typeData.handles, example: typeData.example };
          const esTranslation: Translation = translations.find((t: Translation) => t.language_code === 'es') || 
            { language_code: 'es', title: '', description: '', handles: '', example: '' };
          
          setFormData({
            title: typeData.title,
            description: typeData.description,
            handles: typeData.handles,
            example: typeData.example,
            translations: [enTranslation, esTranslation]
          });
          
          // Handle skills
          const typeWithSkills = lawyerTypes.find(type => (typeof type.id === 'number' ? type.id : type.id.id) === id);
          if (typeWithSkills?.skills && typeWithSkills.skills.length > 0) {
            setSelectedSkill(typeWithSkills.skills[0].id);
          }
        })
        .catch((err) => console.error('Error fetching lawyer type with translations:', err));
      
      // Fetch available skills for this type
      axios.get(`${newServer}skill/available-skills`, { params: { type_id: id } })
        .then((response) => setAvailableSkills(response.data as Skill[]))
        .catch((err) => console.error('Error fetching available skills:', err));
    } else {
      // Adding new
      setFormData({ 
        title: '', 
        description: '', 
        handles: '', 
        example: '',
        translations: [
          { language_code: 'en', title: '', description: '', handles: '', example: '' },
          { language_code: 'es', title: '', description: '', handles: '', example: '' }
        ]
      });
      
      // Get all skills
      axios.get(`${newServer}skill/skills`)
        .then((response) => setAvailableSkills(response.data as Skill[]))
        .catch((err) => console.error('Error fetching skills', err));
    }
    
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
    setEditId(null);
    setFormData({ 
      title: '', 
      description: '', 
      handles: '', 
      example: '',
      translations: [
        { language_code: 'en', title: '', description: '', handles: '', example: '' },
        { language_code: 'es', title: '', description: '', handles: '', example: '' }
      ]
    });
    setSelectedSkill(''); // Resetear a valor vacío
    setTabValue(0);
    
    // Reload data
    setTimeout(() => {
      fetchData();
    }, 2000);
  };

  const handleSubmit = (): void => {
    // Update the main fields with the English translation (base language)
    const enTranslation = formData.translations.find(t => t.language_code === 'en');
    const submitData = {
      ...formData,
      title: enTranslation?.title || formData.title,
      description: enTranslation?.description || formData.description,
      handles: enTranslation?.handles || formData.handles,
      example: enTranslation?.example || formData.example,
      // Only include Spanish translation if it has content
      translations: formData.translations.filter(t => 
        t.language_code === 'es' && (t.title || t.description || t.handles || t.example)
      )
    };
    
    if (editId) {
      // Update lawyer type
      axios.put(`${newServer}/lawyer-types/update-lawyer-type/${editId}`, submitData)
        .then(() => {
          // After updating type, update skills relation
          // Solo enviar el skill seleccionado si existe
          const skillIds = selectedSkill !== '' ? [selectedSkill] : [];
          return axios.post(`${newServer}skill/type-skills`, {
            type_id: editId,
            skill_ids: skillIds
          });
        })
        .then(() => {
          toast.success('Lawyer type and skill updated successfully');
          handleClose();
        })
        .catch(() => toast.error('Error updating lawyer type or skill'));
    } else {
      // Create new lawyer type
      axios.post(`${newServer}/lawyer-types/create-lawyer-type`, submitData)
        .then((response) => {
            const newType = response.data as LawyerType;
             
            
            // After creating type, create skills relation (solo si hay un skill seleccionado)
            if (selectedSkill !== '') {
            return axios.post(`${newServer}skill/type-skills`, {
              type_id: typeof newType.id === 'number' ? newType.id : newType.id.id,
              skill_ids: [selectedSkill]
            }).then(() => newType);
            }
            return newType;
        })
        .then((newType) => {
          toast.success('Lawyer type and skill added successfully');
          handleClose();
        })
        .catch(() => toast.error('Error adding lawyer type or skill'));
    }
  };

  const handleDelete = (id: number): void => {
    // Check if this type has relations with skills
    const hasRelations = typeSkillRelations.some(rel => rel.id_types === id);
    
    const skill_id = typeSkillRelations.find(rel => rel.id_types === id)?.id_skills;
    
    const message = hasRelations 
      ? "This lawyer type has skill relations that will also be deleted. Do you want to continue?"
      : "You won't be able to revert this!";
    
    Swal.fire({
      title: "Are you sure?",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        // If has relations, delete them first
        const deleteRelations = hasRelations 
          ? axios.delete(`${newServer}skill/type-skills/${id}/${skill_id}`)
          : Promise.resolve();
        
        deleteRelations
          .then(() => axios.delete(`${newServer}/lawyer-types/delete-lawyer-type/${id}`))
          .then(() => {
            setLawyerTypes((prev) => prev.filter((item) => item.id !== id));
            // Also remove relations
            setTypeSkillRelations((prev) => prev.filter(rel => rel.id_types !== id));
            
            Swal.fire({
              title: "Deleted!",
              text: "Lawyer type has been deleted.",
              icon: "success"
            });
          })
          .catch(() => toast.error('Error deleting lawyer type or its relations'));
      }
    });
  };

  // Open skills management dialog
  const handleManageSkills = (typeId: number): void => {
    setCurrentTypeId(typeId);
    
    // Get available skills for this type
    axios.get(`${newServer}/skill/available-skills`, { params: { type_id: typeId } })
      .then((response) => {
        setAvailableSkills(response.data as Skill[]);
        
        // Get current skills for this type
        const typeWithSkills = lawyerTypes.find(type => type.id === typeId);
        if (typeWithSkills?.skills && typeWithSkills.skills.length > 0) {
          setSelectedSkill(typeWithSkills.skills[0].id); // Solo tomar el primer skill
        } else {
          setSelectedSkill('');
        }
        
        setSkillsOpen(true);
      })
      .catch((err) => console.error('Error fetching available skills:', err));
  };

  // Handle skills changes
  const handleSkillChange = (event: SelectChangeEvent<number | string>): void => {
    setSelectedSkill(event.target.value as number | '');
  };

  // Save skills relation
  const handleSaveSkills = (): void => {
    if (currentTypeId) {
      // Solo enviar el skill seleccionado si existe
      const skillIds = selectedSkill !== '' ? [selectedSkill] : [];
      
      axios.post(`${newServer}skill/type-skills`, {
        type_id: currentTypeId,
        skill_ids: skillIds
      })
        .then(() => {
          toast.success('Skill updated successfully');
          setSkillsOpen(false);
          fetchData();
        })
        .catch(() => toast.error('Error updating skill'));
    }
  };

  // Remove a single skill from a type
  const handleRemoveSkill = (typeId: number, skillId: number): void => {
    Swal.fire({
      title: "Remove Skill",
      text: "Are you sure you want to remove this skill from the lawyer type?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!"
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${newServer}skill/type-skills/${typeId}/${skillId}`)
          .then(() => {
            toast.success('Skill removed successfully');
            
            // Update the state to reflect the change
            setTypeSkillRelations(prev => 
              prev.filter(rel => !(rel.id_types === typeId && rel.id_skills === skillId))
            );
            
            // Update lawyer types with their skills
            setLawyerTypes(prevTypes => {
              return prevTypes.map(type => {
                if (type.id === typeId && type.skills) {
                  return {
                    ...type,
                    skills: type.skills.filter(skill => skill.id !== skillId)
                  };
                }
                return type;
              });
            });
          })
          .catch(() => toast.error('Error removing skill'));
      }
    });
  };
  
  // Handle translation field changes
  const handleTranslationChange = (langCode: string, field: keyof Translation, value: string): void => {
    setFormData(prev => ({
      ...prev,
      translations: prev.translations.map(translation => 
        translation.language_code === langCode 
          ? { ...translation, [field]: value }
          : translation
      )
    }));
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };
  
  // Handle language change for the main list
  const handleLanguageChange = (event: SelectChangeEvent<string>): void => {
    setCurrentLanguage(event.target.value);
    fetchData();
  };
  
  // =================================================================
  // FUNCIONES PARA GESTIONAR SKILLS
  // =================================================================
  
  const handleSkillOpen = (skill: Skill | null = null, id: number | null = null): void => {
    setSkillEditId(id);
    setSkillTabValue(0);
    
    if (skill && id) {
      // Editing mode - use existing data and initialize translations
      // Try to fetch with translations, but fallback to existing data
      axios.get(`${newServer}skill/admin/skill/${id}?include_translations=true`)
        .then((response) => {
          const skillData = response.data as Skill & { translations?: SkillTranslation[] };
          const translations = skillData.translations || [];
          
          // Ensure we have both languages
          const enTranslation: SkillTranslation = translations.find((t: SkillTranslation) => t.language_code === 'en') || 
            { language_code: 'en', nombre: skillData.nombre, descriptions: skillData.descriptions || '' };
          const esTranslation: SkillTranslation = translations.find((t: SkillTranslation) => t.language_code === 'es') || 
            { language_code: 'es', nombre: '', descriptions: '' };
          
          setSkillFormData({
            nombre: skillData.nombre,
            descriptions: skillData.descriptions || '',
            translations: [enTranslation, esTranslation]
          });
        })
        .catch((err) => {
          console.log('API not available yet, using fallback data');
          // Fallback: use existing skill data
          setSkillFormData({
            nombre: skill.nombre,
            descriptions: skill.descriptions || '',
            translations: [
              { language_code: 'en', nombre: skill.nombre, descriptions: skill.descriptions || '' },
              { language_code: 'es', nombre: '', descriptions: '' }
            ]
          });
        });
    } else {
      // Adding new
      setSkillFormData({
        nombre: '',
        descriptions: '',
        translations: [
          { language_code: 'en', nombre: '', descriptions: '' },
          { language_code: 'es', nombre: '', descriptions: '' }
        ]
      });
    }
    
    setSkillOpen(true);
  };
  
  const handleSkillClose = (): void => {
    setSkillOpen(false);
    setSkillEditId(null);
    setSkillFormData({
      nombre: '',
      descriptions: '',
      translations: [
        { language_code: 'en', nombre: '', descriptions: '' },
        { language_code: 'es', nombre: '', descriptions: '' }
      ]
    });
    setSkillTabValue(0);
    setTimeout(() => fetchAllSkills(), 1000);
  };
  
  const handleSkillSubmit = (): void => {
    // Update main fields with English translation
    const enTranslation = skillFormData.translations.find(t => t.language_code === 'en');
    const submitData = {
      ...skillFormData,
      nombre: enTranslation?.nombre || skillFormData.nombre,
      descriptions: enTranslation?.descriptions || skillFormData.descriptions,
      submerchant_id: 5, // TODO: get from context
      translations: skillFormData.translations.filter(t => 
        t.language_code === 'es' && (t.nombre || t.descriptions)
      )
    };
    
    if (skillEditId) {
      // Update skill
      axios.put(`${newServer}skill/admin/update/${skillEditId}`, submitData)
        .then(() => {
          toast.success('Skill updated successfully');
          handleSkillClose();
        })
        .catch(() => toast.error('Error updating skill'));
    } else {
      // Create new skill
      axios.post(`${newServer}skill/admin/create`, submitData)
        .then(() => {
          toast.success('Skill created successfully');
          handleSkillClose();
        })
        .catch(() => toast.error('Error creating skill'));
    }
  };
  
  const handleSkillDelete = (id: number): void => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${newServer}skill/admin/delete/${id}`)
          .then(() => {
            setAllSkills(prev => prev.filter(skill => skill.id !== id));
            toast.success('Skill deleted successfully');
          })
          .catch(() => toast.error('Error deleting skill'));
      }
    });
  };
  
  const handleSkillTranslationChange = (langCode: string, field: keyof SkillTranslation, value: string): void => {
    setSkillFormData(prev => ({
      ...prev,
      translations: prev.translations.map(translation => 
        translation.language_code === langCode 
          ? { ...translation, [field]: value }
          : translation
      )
    }));
  };
  
  // =================================================================
  // FUNCIONES PARA GESTIONAR LANGUAGES
  // =================================================================
  
  const handleLanguageOpen = (language: Language | null = null, id: number | null = null): void => {
    setLanguageEditId(id);
    setLanguageTabValue(0);
    
    if (language && id) {
      // Editing mode - use existing data and initialize translations
      // Try to fetch with translations, but fallback to existing data
      axios.get(`${newServer}language/admin/language/${id}?include_translations=true`)
        .then((response) => {
          const languageData = response.data as Language & { translations?: LanguageTranslation[] };
          const translations = languageData.translations || [];
          
          // Ensure we have both languages
          const enTranslation: LanguageTranslation = translations.find((t: LanguageTranslation) => t.language_code === 'en') || 
            { language_code: 'en', nombre: languageData.nombre };
          const esTranslation: LanguageTranslation = translations.find((t: LanguageTranslation) => t.language_code === 'es') || 
            { language_code: 'es', nombre: '' };
          
          setLanguageFormData({
            nombre: languageData.nombre,
            codigo: languageData.codigo || '',
            translations: [enTranslation, esTranslation]
          });
        })
        .catch((err) => {
          console.log('API not available yet, using fallback data');
          // Fallback: use existing language data
          setLanguageFormData({
            nombre: language.nombre,
            codigo: language.codigo || '',
            translations: [
              { language_code: 'en', nombre: language.nombre },
              { language_code: 'es', nombre: '' }
            ]
          });
        });
    } else {
      // Adding new
      setLanguageFormData({
        nombre: '',
        codigo: '',
        translations: [
          { language_code: 'en', nombre: '' },
          { language_code: 'es', nombre: '' }
        ]
      });
    }
    
    setLanguageOpen(true);
  };
  
  const handleLanguageClose = (): void => {
    setLanguageOpen(false);
    setLanguageEditId(null);
    setLanguageFormData({
      nombre: '',
      codigo: '',
      translations: [
        { language_code: 'en', nombre: '' },
        { language_code: 'es', nombre: '' }
      ]
    });
    setLanguageTabValue(0);
    setTimeout(() => fetchAllLanguages(), 1000);
  };
  
  const handleLanguageSubmit = (): void => {
    // Update main fields with English translation
    const enTranslation = languageFormData.translations.find(t => t.language_code === 'en');
    const submitData = {
      ...languageFormData,
      nombre: enTranslation?.nombre || languageFormData.nombre,
      submerchant_id: 5, // TODO: get from context
      translations: languageFormData.translations.filter(t => 
        t.language_code === 'es' && t.nombre
      )
    };
    
    if (languageEditId) {
      // Update language
      axios.put(`${newServer}language/admin/update/${languageEditId}`, submitData)
        .then(() => {
          toast.success('Language updated successfully');
          handleLanguageClose();
        })
        .catch(() => toast.error('Error updating language'));
    } else {
      // Create new language
      axios.post(`${newServer}language/admin/create`, submitData)
        .then(() => {
          toast.success('Language created successfully');
          handleLanguageClose();
        })
        .catch(() => toast.error('Error creating language'));
    }
  };
  
  const handleLanguageDelete = (id: number): void => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${newServer}language/admin/delete/${id}`)
          .then(() => {
            setAllLanguages(prev => prev.filter(lang => lang.id !== id));
            toast.success('Language deleted successfully');
          })
          .catch(() => toast.error('Error deleting language'));
      }
    });
  };
  
  const handleLanguageTranslationChange = (langCode: string, field: keyof LanguageTranslation, value: string): void => {
    setLanguageFormData(prev => ({
      ...prev,
      translations: prev.translations.map(translation => 
        translation.language_code === langCode 
          ? { ...translation, [field]: value }
          : translation
      )
    }));
  };

  return (
    <Grid className='bg-white max-h-full' style={FONT_STYLE}>
      <Header />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} style={FONT_STYLE}>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
          <h1 style={FONT_STYLE}>Directory Management</h1>
        </div>
      </Box>
      
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        <FormControlLabel
          control={<Switch checked={isRatingEnabled} onChange={handleToggle} color='primary' />}
          label='Enable Rating Filter'
          style={FONT_STYLE}
        />
      </div>
      
      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={mainTabValue} onChange={(e, newValue) => setMainTabValue(newValue)}>
          <Tab label="Professional Types" />
          <Tab label="Skills" />
          <Tab label="Languages" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {/* Professional Types Tab */}
        {mainTabValue === 0 && (
          <>
            <Button variant='contained' color='primary' onClick={() => handleOpen()} style={FONT_STYLE}>
              Add Professional Type
            </Button>
            <TableContainer 
              sx={{":hover": {backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer'}}}  
              component={Paper} 
              className='mt-4'
            >
              <Table>
                <TableHead sx={{color: 'white'}}>
                  <TableRow sx={{color: 'white'}}>
                    <TableCell sx={{color: 'white'}} className='text-white'>Title</TableCell>
                    <TableCell sx={{color: 'white'}} className='text-white'>Description</TableCell>
                    <TableCell sx={{color: 'white'}} className='text-white'>Handles</TableCell>
                    <TableCell sx={{color: 'white'}} className='text-white'>Example</TableCell>
                    <TableCell sx={{color: 'white'}} className='text-white'>Skill</TableCell>
                    <TableCell sx={{color: 'white'}} className='text-white'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lawyerTypes.map((lawyer) => (
                    <TableRow 
                      key={typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id} 
                      sx={{":hover": {backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer', color: 'white'}}}
                    >
                      <TableCell style={FONT_STYLE}>{lawyer.title}</TableCell>
                      <TableCell style={FONT_STYLE}>{lawyer.description}</TableCell>
                      <TableCell style={FONT_STYLE}>{lawyer.handles}</TableCell>
                      <TableCell style={FONT_STYLE}>{lawyer.example}</TableCell>
                      <TableCell>
                        {lawyer.skills && lawyer.skills.length > 0 ? (
                          <Chip 
                            label={lawyer.skills[0].nombre} 
                            onDelete={() => handleRemoveSkill(typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id, lawyer.skills![0].id)}
                            color="primary"
                            size="small"
                            style={{ margin: '2px' }}
                          />
                        ) : (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => handleManageSkills(typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id)}
                          >
                            Add Skill
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button onClick={() => handleOpen(lawyer, typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id)}>Edit</Button>
                        <Button color='secondary' onClick={() => handleDelete(typeof lawyer.id === 'number' ? lawyer.id : lawyer.id.id)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Skills Tab */}
        {mainTabValue === 1 && (
          <>
            <Button variant='contained' color='primary' onClick={() => handleSkillOpen()} style={FONT_STYLE}>
              Add Skill
            </Button>
            <TableContainer component={Paper} className='mt-4'>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className='text-white'>ID</TableCell>
                    <TableCell className='text-white'>Name</TableCell>
                    <TableCell className='text-white'>Description</TableCell>
                    <TableCell className='text-white'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allSkills.map((skill) => (
                    <TableRow key={skill.id}>
                      <TableCell style={FONT_STYLE}>{skill.id}</TableCell>
                      <TableCell style={FONT_STYLE}>{skill.nombre}</TableCell>
                      <TableCell style={FONT_STYLE}>{skill.descriptions || '-'}</TableCell>
                      <TableCell>
                        <Button onClick={() => handleSkillOpen(skill, skill.id)}>Edit</Button>
                        <Button color='secondary' onClick={() => handleSkillDelete(skill.id)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Languages Tab */}
        {mainTabValue === 2 && (
          <>
            <Button variant='contained' color='primary' onClick={() => handleLanguageOpen()} style={FONT_STYLE}>
              Add Language
            </Button>
            <TableContainer component={Paper} className='mt-4'>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className='text-white'>ID</TableCell>
                    <TableCell className='text-white'>Name</TableCell>
                    <TableCell className='text-white'>Code</TableCell>
                    <TableCell className='text-white'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allLanguages.map((language) => (
                    <TableRow key={language.id}>
                      <TableCell style={FONT_STYLE}>{language.id}</TableCell>
                      <TableCell style={FONT_STYLE}>{language.nombre}</TableCell>
                      <TableCell style={FONT_STYLE}>{language.codigo || '-'}</TableCell>
                      <TableCell>
                        <Button onClick={() => handleLanguageOpen(language, language.id)}>Edit</Button>
                        <Button color='secondary' onClick={() => handleLanguageDelete(language.id)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>

      {/* Add/Edit Lawyer Type Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle style={FONT_STYLE}>{editId ? 'Edit Lawyer Type' : 'Add Lawyer Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="English" />
              <Tab label="Español" />
            </Tabs>
          </Box>
          
          {/* English Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <TextField 
                label='Title (English)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'en')?.title || ''} 
                onChange={(e) => handleTranslationChange('en', 'title', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3}  
                label='Description (English)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'en')?.description || ''} 
                onChange={(e) => handleTranslationChange('en', 'description', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3} 
                label='Handles (English)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'en')?.handles || ''} 
                onChange={(e) => handleTranslationChange('en', 'handles', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3} 
                label='Example (English)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'en')?.example || ''} 
                onChange={(e) => handleTranslationChange('en', 'example', e.target.value)}
              />
            </Box>
          )}
          
          {/* Spanish Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <TextField 
                label='Título (Español)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'es')?.title || ''} 
                onChange={(e) => handleTranslationChange('es', 'title', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3}  
                label='Descripción (Español)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'es')?.description || ''} 
                onChange={(e) => handleTranslationChange('es', 'description', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3} 
                label='Maneja (Español)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'es')?.handles || ''} 
                onChange={(e) => handleTranslationChange('es', 'handles', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3} 
                label='Ejemplo (Español)' 
                fullWidth 
                margin='dense' 
                value={formData.translations.find(t => t.language_code === 'es')?.example || ''} 
                onChange={(e) => handleTranslationChange('es', 'example', e.target.value)}
              />
            </Box>
          )}
          
          <FormControl fullWidth margin='dense'>
            <InputLabel id="skills-select-label">Skill</InputLabel>
            <Select
              labelId="skills-select-label"
              value={selectedSkill}
              onChange={handleSkillChange}
              renderValue={(selected) => {
                if (selected === null) {
                  return <em>Select a skill</em>;
                }
                const skill = availableSkills.find(s => s.id === selected);
                return skill ? skill.nombre : 'Unknown';
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableSkills.map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  {skill.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} color='primary'>{editId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Skills Dialog */}
      <Dialog open={skillsOpen} onClose={() => setSkillsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={FONT_STYLE}>Manage Skill</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin='dense'>
            <InputLabel id="manage-skills-select-label">Skill</InputLabel>
            <Select
              labelId="manage-skills-select-label"
              value={selectedSkill}
              onChange={handleSkillChange}
              renderValue={(selected) => {
                if (selected === 0) {
                  return <em>Select a skill</em>;
                }
                const skill = availableSkills.find(s => s.id === selected);
                return skill ? skill.nombre : 'Unknown';
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableSkills.map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  {skill.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkillsOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSkills} color='primary'>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Skill Dialog */}
      <Dialog open={skillOpen} onClose={handleSkillClose} maxWidth="md" fullWidth>
        <DialogTitle style={FONT_STYLE}>{skillEditId ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={skillTabValue} onChange={(e, newValue) => setSkillTabValue(newValue)}>
              <Tab label="English" />
              <Tab label="Español" />
            </Tabs>
          </Box>
          
          {/* English Tab */}
          {skillTabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <TextField 
                label='Name (English)' 
                fullWidth 
                margin='dense' 
                value={skillFormData.translations.find(t => t.language_code === 'en')?.nombre || ''} 
                onChange={(e) => handleSkillTranslationChange('en', 'nombre', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3}  
                label='Description (English)' 
                fullWidth 
                margin='dense' 
                value={skillFormData.translations.find(t => t.language_code === 'en')?.descriptions || ''} 
                onChange={(e) => handleSkillTranslationChange('en', 'descriptions', e.target.value)}
              />
            </Box>
          )}
          
          {/* Spanish Tab */}
          {skillTabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <TextField 
                label='Nombre (Español)' 
                fullWidth 
                margin='dense' 
                value={skillFormData.translations.find(t => t.language_code === 'es')?.nombre || ''} 
                onChange={(e) => handleSkillTranslationChange('es', 'nombre', e.target.value)}
              />
              <TextField 
                multiline 
                rows={3}  
                label='Descripción (Español)' 
                fullWidth 
                margin='dense' 
                value={skillFormData.translations.find(t => t.language_code === 'es')?.descriptions || ''} 
                onChange={(e) => handleSkillTranslationChange('es', 'descriptions', e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkillClose}>Cancel</Button>
          <Button onClick={handleSkillSubmit} color='primary'>{skillEditId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Language Dialog */}
      <Dialog open={languageOpen} onClose={handleLanguageClose} maxWidth="md" fullWidth>
        <DialogTitle style={FONT_STYLE}>{languageEditId ? 'Edit Language' : 'Add Language'}</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={languageTabValue} onChange={(e, newValue) => setLanguageTabValue(newValue)}>
              <Tab label="English" />
              <Tab label="Español" />
            </Tabs>
          </Box>
          
          {/* English Tab */}
          {languageTabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <TextField 
                label='Name (English)' 
                fullWidth 
                margin='dense' 
                value={languageFormData.translations.find(t => t.language_code === 'en')?.nombre || ''} 
                onChange={(e) => handleLanguageTranslationChange('en', 'nombre', e.target.value)}
              />
              <TextField 
                label='Language Code' 
                fullWidth 
                margin='dense' 
                value={languageFormData.codigo} 
                onChange={(e) => setLanguageFormData({...languageFormData, codigo: e.target.value})}
                placeholder="e.g., EN, ES, FR"
              />
            </Box>
          )}
          
          {/* Spanish Tab */}
          {languageTabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <TextField 
                label='Nombre (Español)' 
                fullWidth 
                margin='dense' 
                value={languageFormData.translations.find(t => t.language_code === 'es')?.nombre || ''} 
                onChange={(e) => handleLanguageTranslationChange('es', 'nombre', e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLanguageClose}>Cancel</Button>
          <Button onClick={handleLanguageSubmit} color='primary'>{languageEditId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default DirectorySettings;